import React, { useState } from "react";
import styled from "styled-components";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { RadioDropdownItem } from "@sonder/ui/source/styled/fragments/RadioDropdownItem";
import { SidebarButton } from "@sonder/ui/source/styled/fragments/Sidebar";
import { setCurrentUser } from "auth/actions";
import { useStore as useAuthStore } from "auth/store";
import { useGetCitiesQuery } from "core/queries/GetCities";
import { useUpdateUser } from "core/queries/UpdateUser";
import { setFilters as setScheduleFilters } from "schedule/actions";
import { initialState as initialScheduleState } from "schedule/reducer";
import { useStore as useScheduleStore } from "schedule/store";
import { bulkSetFilters } from "tasks/actions";
import { initialState as initialTaskState } from "tasks/reducer";
import { useStore as useTaskStore } from "tasks/store";

const StyledCityAbbreviation = styled.span`
  font-size: 1.143rem;
`;

const StyledSubmitButton = styled(Button)`
  margin-left: auto;
`;

export const CityPicker = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const { dispatch: dispatchAuth, state: authState } = useAuthStore();
  const { dispatch: dispatchTasks } = useTaskStore();
  const { dispatch: dispatchSchedule } = useScheduleStore();
  const currentUser = authState?.user;
  const userId = currentUser?.id;
  const cityId = currentUser?.cityId;
  const isCityPickerEnabled = currentUser?.isLeadOrManager;
  const [newSelectedCityId, setNewSelectedCityId] = useState<string | null>(
    null,
  );
  const { data, loading } = useGetCitiesQuery({
    skip: !isCityPickerEnabled,
    variables: {
      withAsanaOpsProjectIdOnly: true,
    },
  });
  const [onUpdateUser, { loading: isUpdating }] = useUpdateUser({
    onCompleted: responseData => {
      const user = responseData?.updateUser?.user;
      // Wipe filters and update cityID for Tasks
      dispatchTasks(
        bulkSetFilters({
          ...initialTaskState.filters,
          city: newSelectedCityId,
        }),
      );
      // Wipe filters for Scheduler.
      dispatchSchedule(setScheduleFilters(initialScheduleState.filters));

      // Update User with new cityID
      if (currentUser)
        dispatchAuth(
          setCurrentUser({
            ...currentUser,
            cityId: newSelectedCityId,
            featureFlags: user?.featureFlags || [],
            timezone: user?.tomTeamMember?.city?.timezone || "",
          }),
        );
    },
  });
  const cities = data?.cities || [];
  const cachedSelectedCity = cities.find(city => city.id === cityId);
  const newSelectedCity = cities.find(city => city.id === newSelectedCityId);
  const filterCities = cities.filter(city => {
    return (
      city.name.toLowerCase().includes(searchTerm.toLocaleLowerCase()) ||
      city.abbreviation.toLowerCase().includes(searchTerm.toLocaleLowerCase())
    );
  });
  const isSelectedCityChanged =
    newSelectedCityId && newSelectedCityId !== cityId;

  if (!isCityPickerEnabled || !userId || !cityId || loading) return null;

  return (
    <Dropdown
      trigger={
        <SidebarButton disabled={!isCityPickerEnabled}>
          <StyledCityAbbreviation>
            {cachedSelectedCity?.abbreviation || "?"}
          </StyledCityAbbreviation>
        </SidebarButton>
      }
    >
      {({ closePortal, isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align="left">
            <FilterTextArea
              autoFocus
              placeholder="Search cities"
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
              }}
            />
            {filterCities.length === 0 ? (
              <FilterEmpty>{`No Results for ${searchTerm}.`}</FilterEmpty>
            ) : (
              <FilterButtonList>
                {filterCities.map(city => {
                  return (
                    <RadioDropdownItem
                      checked={
                        newSelectedCity?.id === city.id ||
                        (!newSelectedCity && cachedSelectedCity?.id === city.id)
                      }
                      key={city.id}
                      onClick={() => {
                        setNewSelectedCityId(city.id);
                      }}
                    >
                      {city.name}
                    </RadioDropdownItem>
                  );
                })}
              </FilterButtonList>
            )}
            <FilterFooter>
              <StyledSubmitButton
                disabled={!isSelectedCityChanged || isUpdating}
                type="button"
                onClick={async () => {
                  await onUpdateUser({
                    variables: {
                      id: userId,
                      tomTeamMemberCityId: newSelectedCityId,
                    },
                  });
                  closePortal();
                }}
              >
                {isUpdating ? "Switching..." : "Switch"}
              </StyledSubmitButton>
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
